import { Alert, AlertDescription, AlertIcon } from '@chakra-ui/alert';
import React, { FC, useMemo } from 'react';

import { EMPTY_ADDRESS } from '@app/constants/common';
import { usePairDetails } from '@app/hooks/pair/usePairDetails';
import { InputsState } from '@app/screens/liquidity/AddLiquidity/hooks';

interface Props {
  pairAddress: string | undefined;
  state: InputsState;
  fromTokenBalance: string | undefined;
  toTokenBalance: string | undefined;
}

export const Notifications: FC<Props> = ({
  pairAddress,
  state,
  fromTokenBalance,
  toTokenBalance
}) => {
  const { data: pairDetails, isLoading } = usePairDetails({
    id: pairAddress ? pairAddress.toLowerCase() : null
  });

  const { message, status } = useMemo(() => {
    if (isLoading) {
      return {
        message: '',
        status: null
      };
    }

    if (
      state.from.token &&
      state.to.token &&
      (!toTokenBalance ||
        +toTokenBalance < +state.to.amount ||
        !fromTokenBalance ||
        +fromTokenBalance < +state.from.amount)
    ) {
      return {
        status: 'error',
        message: 'Insufficient balance'
      };
    }

    if (
      (!pairDetails || pairDetails?.id === EMPTY_ADDRESS) &&
      state.from.token &&
      state.to.token
    ) {
      return {
        status: 'info',
        message:
          'You are the first liquidity provider! The token ratio that you choose here will set the price on this pool'
      };
    }

    return {
      message: '',
      status: null
    };
  }, [
    fromTokenBalance,
    isLoading,
    pairDetails,
    state.from.amount,
    state.from.token,
    state.to.amount,
    state.to.token,
    toTokenBalance
  ]);

  if (status) {
    return (
      <Alert
        status="error"
        display="flex"
        border="1px solid"
        borderColor={status === 'error' ? 'danger.800' : 'neutral.500'}
        mt="16px"
        borderRadius="10px"
        py="10px"
        px="12px"
      >
        {status === 'error' ? (
          <AlertIcon w="16px" h="16px" mr="8px" color="orange" />
        ) : (
          <AlertIcon w="16px" h="16px" mr="8px" color="whiteOpacity.50" />
        )}

        <AlertDescription color="danger.200">{message}</AlertDescription>
      </Alert>
    );
  }

  return null;
};
